import React, { useContext, useEffect } from 'react'

import { useTheme } from '@mui/material/styles'
import get from 'lodash.get'
import type { SubmitHandler } from 'react-hook-form'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import ChildFormFields from './ChildFormFields'

import { ContinueButton, CTAWrapper } from 'components/Template/Buttons/CreationFlowButtons'
import InterviewCard from 'components/Template/InterviewCard'
import Typography from 'components/Template/MuiTypography'
import { SnackbarContext } from 'contexts/SnackbarProvider'
import useIsDesktop from 'hooks/useIsDesktop'
import { useInterviewBatchMutation } from 'pages/PostInvite/queries'
import { useArtifactDetailQuery } from 'queries'
import { page } from 'utils/monitoring'

export type FormValue = {
    subjects: Subject[]
}

export default function Subject() {
    const theme = useTheme()
    const navigate = useNavigate()
    const snackbarContext = useContext(SnackbarContext)
    const { data: artifact } = useArtifactDetailQuery()
    const isDesktop = useIsDesktop()
    const { isLoading, mutateAsync }: { isLoading: boolean; mutateAsync: (data: any) => Promise<any> } =
        useInterviewBatchMutation(artifact.hashid, 'subject', null)

    const initialValues: FormValue = {
        subjects: artifact.interviews.map(
            (i: Interview) =>
                get(i, 'subject') || { first_name: null, last_name: null, birthday: null, is_expected_baby: false }
        )
    }

    const {
        formState: { errors },
        handleSubmit,
        register,
        setValue,
        watch
    } = useForm({
        defaultValues: initialValues
    })

    useEffect(() => {
        page('Subject', {})
    }, [])

    const onSubmit: SubmitHandler<FormValue> = (data) => {
        mutateAsync(
            artifact.interviews.map((interview: Interview, index: number) => ({
                id: interview.id,
                subject: data.subjects[index]
            }))
        )
            .then(() => navigate(`/dashboard/${artifact.hashid}/producer-interviewed/`))
            .catch(() => snackbarContext.error('Unable to save the details.'))
    }

    const cardsContainerStyle: React.CSSProperties =
        artifact.interviews.length > 1
            ? {
                  display: 'grid',
                  gridTemplateColumns: isDesktop ? '1fr 1fr' : '1fr',
                  rowGap: theme.spacing(1),
                  columnGap: theme.spacing(4)
              }
            : { display: 'flex', justifyContent: 'center' }

    return (
        <div style={{ marginTop: theme.spacing(4) }}>
            <Typography variant='h2' style={{ textAlign: 'center' }}>
                Add details about the {artifact.interviews.length > 1 ? 'children' : 'child'}
            </Typography>
            <Typography variant='body1' style={{ textAlign: 'center' }}>
                This helps your interviewer prepare for the conversation.
            </Typography>
            <form onSubmit={handleSubmit(onSubmit)} id='form-subjects' style={{ marginTop: theme.spacing(4) }}>
                <div style={cardsContainerStyle}>
                    {artifact.interviews.map((_: Interview, index: number) => {
                        return (
                            <div key={`interview-card-${index}`} style={{ flexGrow: 0.4 }}>
                                <InterviewCard title={`Interview  #${index + 1}`}>
                                    <ChildFormFields
                                        watch={watch}
                                        setValue={setValue}
                                        index={index}
                                        register={register}
                                        errors={errors}
                                    />
                                </InterviewCard>
                            </div>
                        )
                    })}
                </div>
            </form>
            <CTAWrapper>
                <ContinueButton disabled={isLoading} loading={isLoading} form='form-subjects' />
            </CTAWrapper>
        </div>
    )
}
